import { Component, Input, Output, OnInit, TemplateRef, ElementRef, EventEmitter, HostBinding, ViewChild, Renderer } from '@angular/core';
import * as _ from 'lodash';
import { CfCoreComponent } from '../core/core.component';
import { IconModel } from '../../models/icon/icon.model';
import { MenuModel } from '../../models/menu/menu.model';
import { ToolbarModel } from '../../models/toolbar/toolbar.model';
import { ToolbarStylingModel } from '../../models/toolbar/toolbar-styling.model';
import { ToolbarTemplates } from '../../templates/toolbar.template';
import { TemplateService } from '../../services/template-service/template.service';
/**
 * <p>Toolbar component based on Angular Material Toolbar and has a lot of extanded options</p>
 * <p>Example of using:</p>
 * <pre>
 * <code><</code>cf-toolbar [properties]="myToolbar" [styling]="myToolbarStyles"<code>></code>
 * <code><</code><code>/</code>cf-toolbar<code>></code>
 * </pre>
 */
export class CfToolbarComponent extends CfCoreComponent implements OnInit {
/**
 * @hidden 
 * Binding class to Toolbar host element
 */
toolbarMaximizedClass = '';
/**
 * <p>Example of model:</p>
 * <code><pre>{{'{'}}
 * 	type: 'info',
 * 	info: {{'{'}}
 * 		show: true,
 * 		icon: this.myInfoIcon,
 * 		order: 0
 * 	{{'}'}},
 * 	content: {{'{'}}
 * 		title: 'This is my toolbar title',
 *  	template: this.myToolbarTemplate,
 * 		order: 1
 * 	{{'}'}},
 * 	actions: {{'{'}}
 * 		show: true,
 * 		menu: this.myToobarMenu,
 * 		order: 2
 * 	{{'}'}},
 * 	help: {{'{'}}
 * 		show: true,
 * 		icon: this.myHelpIcon,
 * 		order: 3
 * 	{{'}'}},
 * 	close: {{'{'}}
 * 		show: true,
 * 		icon: this.myCloseIcon,
 * 		order: 4
 * 	{{'}'}},
 * 	maximization: {{'{'}}
 * 		show: true,
 * 		maximized: false,
 * 		maximizeIcon: this.myMaxIcon,
 * 		minimizeIcon: this.myMinIcon,
 * 		order: 5
 * 	{{'}'}},
 * 	expandable: {{'{'}}
 * 		show: true,
 * 		expanded: true,
 * 		icon: this.myexpandableIcon
 * 		order: 6
 * 	{{'}'}}
 * {{'}'}}</pre></code>
 * <h4>type: string</h4>
 * Toolbar type. Can be: info, warning, error.
 * <h4>info: object</h4>
 * Optional info section.
 * <h4>info.show: boolean</h4>
 * To show / hide info section.
 * <h4>info.icon: IconModel</h4>
 * IconModel data for info icon.
 * <h4>info.order: number</h4>
 * Order of info section. Means flex system order property. Order numbers can be: 0, 1, 2, 3, 4, 5, 6.
 * <h4>content: object</h4>
 * Optional content section.
 * <h4>content.show: boolean</h4>
 * To show / hide toolbar content section.
 * <h4>content.title: string</h4>
 * Title for content section.
 * <h4>content.template: TemplateRef{{'<'}}any{{'>'}}</h4>
 * Toolbar content template.
 * <h4>content.order: number</h4>
 * Order of content section. Means flex system order property. Order numbers can be: 0, 1, 2, 3, 4, 5, 6.
 * <h4>actions: object</h4>
 * Optional actions section.
 * <h4>actions.show: boolean</h4>
 * To show / hide actions section.
 * <h4>actions.menu: MenuModel</h4>
 * MenuModel component for actions section.
 * <h4>actions.order: number</h4>
 * Order of actions section. Means flex system order property. Order numbers can be: 0, 1, 2, 3, 4, 5, 6.
 * <h4>close: object</h4>
 * Optional close icon section.
 * <h4>close.show: boolean</h4>
 * To show / hide close icon section.
 * <h4>close.icon: IconModel</h4>
 * IconModel data for close icon.
 * <h4>close.order: number</h4>
 * Order of close icon section. Means flex system order property. Order numbers can be: 0, 1, 2, 3, 4, 5, 6.
 * <h4>help: object</h4>
 * Optional help icon section.
 * <h4>help.show: boolean</h4>
 * To show / hide help icon section.
 * <h4>icon: IconModel</h4>
 * IconModel data for help icon.
 * <h4>help.order: number</h4>
 * Order of help icon section. Means flex system order property. Order numbers can be: 0, 1, 2, 3, 4, 5, 6.
 * <h4>maximization: object</h4>
 * Optional maximize/minimize icons section.
 * <h4>maximization.show: boolean</h4>
 * To show / hide maximize/minimize icons section.
 * <h4>maximization.maximized: boolean</h4>
 * Whether toolbar must be maximized.
 * <h4>maximization.minimizeIcon: IconModel</h4>
 * IconModel data for minimizeIcon.
 * <h4>maximization.maximizeIcon: IconModel</h4>
 * IconModel data for maximizeIcon.
 * <h4>maximization.order: number</h4>
 * Order of maximize/minimize icons section. Means flex system order property. Order numbers can be: 0, 1, 2, 3, 4, 5, 6.
 * <h4>expandable: object</h4>
 * Optional expandable icon section.
 * <h4>expandable.show: boolean</h4>
 * To show / hide expandable icon section.
 * <h4>expandable.expanded: boolean</h4>
 * To set toolbar to be expanded.
 * <h4>expandable.icon: IconModel</h4>
 * IconModel data for expandable icon.
 * <h4>expandable.order: number</h4>
 * Order of expandable icon section. Means flex system order property. Order numbers can be: 0, 1, 2, 3, 4, 5, 6.
 */
properties: ToolbarModel;
/**
 * Styling object
 * <code><pre>
 * toolbar?: {{'{'}}                             // Styling for md-toolbar
 * 	info?: {{'{'}}                               // Styling for info section
 * 		class?: string;                      // Classes for info section
 * 		dynamicClass?: any;                  // Dynamic classes for info section
 * 		icon?: IconStylingModel;             // IconStylingModel for info icon
 * 	{{'}'}};
 * 	content?: {{'{'}}                            // Styling for content section
 * 		class?: string;                      // Classes for content section
 * 		dynamicClass?: any;                  // Dynamic classes for content section
 * 		title?: StylingModel;                // StylingModel for title
 * 		template?: StylingModel;             // StylingModel for template
 * 	{{'}'}};
 * 	actions?: {{'{'}}                            // Styling for actions section
 * 		class?: string;                      // Classes for actions section
 * 		dynamicClass?: any;                  // Dynamic classes for actions section
 * 		menu?: MenuStylingModel;             // MenuStylingModel for menu section
 * 	{{'}'}};
 * 	maximizing?: {{'{'}}                         // Styling for maximizing section
 * 		class?: string;                      // Classes for maximizing section
 * 		dynamicClass?: any;                  // Dynamic classes for maximizing section
 * 		minimizeIcon?: IconStylingModel;     // IconStylingModel for minimize icon
 * 		maximizeIcon?: IconStylingModel;     // IconStylingModel for maximize icon
 * 	{{'}'}};
 * 	expandable?: {{'{'}}                         // Styling for expandable section
 * 		class?: string;                      // Classes for expandable section
 * 		dynamicClass?: any;                  // Dynamic classes for expandable section
 * 		icon?: IconStylingModel;             // IconStylingModel for expandable icon
 * 	{{'}'}};
 * 	help?: {{'{'}}                               // Styling for help section
 * 		class?: string;                      // Classes for help section
 * 		dynamicClass?: any;                  // Dynamic classes for help section
 * 		icon?: IconStylingModel;             // IconStylingModel for help icon
 * 	{{'}'}};
 * 	close?: {{'{'}}                              // Styling for close section
 * 		class?: string;                      // Classes for close section
 * 		dynamicClass?: any;                  // Dynamic classes for close section
 * 		icon?: IconStylingModel;             // IconStylingModel for close icon
 * 	{{'}'}};
 * {{'}'}};
 * </pre></code>
 */
styling: ToolbarStylingModel;
/**
 * Type of the toolbar. Can be: info, warning, error.
 * @param {?} value
 * @return {?}
 */
set type(value: string) { this.properties.type = value; }
/**
 * Wheather to show info section.
 * @param {?} value
 * @return {?}
 */
set showInfo(value: boolean) {
    if (this.properties.info == null) { this.properties.info = {}; }
    this.properties.info.show = value;
  }
/**
 * Order of info section. Means flex system order property. Order numbers can be: 0, 1, 2, 3, 4, 5, 6.
 * @param {?} value
 * @return {?}
 */
set infoOrder(value: number) {
    if (this.properties.info == null) { this.properties.info = {}; }
    this.properties.info.order = value;
  }
/**
 * Info icon name.
 * @param {?} value
 * @return {?}
 */
set infoIconName(value: string) {
    if (this.properties.info == null) { this.properties.info = {}; }
    if (this.properties.info.icon == null) { this.properties.info.icon = new IconModel(); }
    this.properties.info.icon.name = value;
  }
/**
 * Info icon size.
 * @param {?} value
 * @return {?}
 */
set infoIconSize(value: string) {
    if (this.properties.info == null) { this.properties.info = {}; }
    if (this.properties.info.icon == null) { this.properties.info.icon = new IconModel(); }
    this.properties.info.icon.size = value;
  }
/**
 * Wheather to show content section.
 * @param {?} value
 * @return {?}
 */
set showContent(value: boolean) {
    if (this.properties.content == null) { this.properties.content = {}; }
    this.properties.content.show = value;
  }
/**
 * Title of the toolbar content section.
 * @param {?} value
 * @return {?}
 */
set title(value: string) {
    if (this.properties.content == null) { this.properties.content = {}; }
    this.properties.content.title = value;
  }
/**
 * Template of the toolbar content section.
 * @param {?} value
 * @return {?}
 */
set template(value: TemplateRef<any>) {
    if (this.properties.content == null) { this.properties.content = {}; }
    this.properties.content.template = value;
  }
/**
 * Order of content section. Means flex system order property. Order numbers can be: 0, 1, 2, 3, 4, 5, 6.
 * @param {?} value
 * @return {?}
 */
set contentOrder(value: number) {
    if (this.properties.content == null) { this.properties.content = {}; }
    this.properties.content.order = value;
  }
/**
 * Wheather to show actions section.
 * @param {?} value
 * @return {?}
 */
set showActions(value: boolean) {
    if (this.properties.actions == null) { this.properties.actions = {}; }
    this.properties.actions.show = value;
  }
/**
 * Order of actions section. Means flex system order property. Order numbers can be: 0, 1, 2, 3, 4, 5, 6.
 * @param {?} value
 * @return {?}
 */
set actionsOrder(value: number) {
    if (this.properties.actions == null) { this.properties.actions = {}; }
    this.properties.actions.order = value;
  }
/**
 * Actions menu trigger icon name.
 * @param {?} value
 * @return {?}
 */
set menuIconName(value: string) {
    if (this.properties.actions == null) { this.properties.actions = {}; }
    if (this.properties.actions.menu == null) { this.properties.actions.menu = new MenuModel(); }
    if (this.properties.actions.menu.triggerIcon == null) { this.properties.actions.menu = new IconModel(); }
    this.properties.actions.menu.triggerIcon.name = value;
  }
/**
 * Actions menu trigger icon size.
 * @param {?} value
 * @return {?}
 */
set menuIconSize(value: string) {
    if (this.properties.actions == null) { this.properties.actions = {}; }
    if (this.properties.actions.menu == null) { this.properties.actions.menu = new MenuModel(); }
    if (this.properties.actions.menu.triggerIcon == null) { this.properties.actions.menu = new IconModel(); }
    this.properties.actions.menu.triggerIcon.size = value;
  }
/**
 * Wheather to show help section.
 * @param {?} value
 * @return {?}
 */
set showHelp(value: boolean) {
    if (this.properties.help == null) { this.properties.help = {}; }
    this.properties.help.show = value;
  }
/**
 * Order of help section. Means flex system order property. Order numbers can be: 0, 1, 2, 3, 4, 5, 6.
 * @param {?} value
 * @return {?}
 */
set helpOrder(value: number) {
    if (this.properties.help == null) { this.properties.help = {}; }
    this.properties.help.order = value;
  }
/**
 * help icon name.
 * @param {?} value
 * @return {?}
 */
set helpIconName(value: string) {
    if (this.properties.help == null) { this.properties.help = {}; }
    if (this.properties.help.icon == null) { this.properties.help.icon = new IconModel(); }
    this.properties.help.icon.name = value;
  }
/**
 * help icon size.
 * @param {?} value
 * @return {?}
 */
set helpIconSize(value: string) {
    if (this.properties.help == null) { this.properties.help = {}; }
    if (this.properties.help.icon == null) { this.properties.help.icon = new IconModel(); }
    this.properties.help.icon.size = value;
  }
/**
 * Wheather to show close section.
 * @param {?} value
 * @return {?}
 */
set showClose(value: boolean) {
    if (this.properties.close == null) { this.properties.close = {}; }
    this.properties.close.show = value;
  }
/**
 * Order of close section. Means flex system order property. Order numbers can be: 0, 1, 2, 3, 4, 5, 6.
 * @param {?} value
 * @return {?}
 */
set closeOrder(value: number) {
    if (this.properties.close == null) { this.properties.close = {}; }
    this.properties.close.order = value;
  }
/**
 * Close icon name.
 * @param {?} value
 * @return {?}
 */
set closeIconName(value: string) {
    if (this.properties.close == null) { this.properties.close = {}; }
    if (this.properties.close.icon == null) { this.properties.close.icon = new IconModel(); }
    this.properties.close.icon.name = value;
  }
/**
 * Close icon size.
 * @param {?} value
 * @return {?}
 */
set closeIconSize(value: string) {
    if (this.properties.close == null) { this.properties.close = {}; }
    if (this.properties.close.icon == null) { this.properties.close.icon = new IconModel(); }
    this.properties.close.icon.size = value;
  }
/**
 * Wheather to show toggle section of maximize/minimize.
 * @param {?} value
 * @return {?}
 */
set showMaximization(value: boolean) {
    if (this.properties.maximization == null) { this.properties.maximization = {}; }
    this.properties.maximization.show = value;
  }
/**
 * Order of maximization section. Means flex system order property. Order numbers can be: 0, 1, 2, 3, 4, 5, 6.
 * @param {?} value
 * @return {?}
 */
set maximizationOrder(value: number) {
    if (this.properties.maximization == null) { this.properties.maximization = {}; }
    this.properties.maximization.order = value;
  }
/**
 * Wheather toolbar is in maximized mode.
 * @param {?} value
 * @return {?}
 */
set maximized(value: boolean) {
    if (this.properties.maximization == null) { this.properties.maximization = {}; }
    this.properties.maximization.maximized = value;
  }
/**
 * Maximize icon name.
 * @param {?} value
 * @return {?}
 */
set maximizeIconName(value: string) {
    if (this.properties.maximization == null) { this.properties.maximization = {}; }
    if (this.properties.maximization.maximizeIcon == null) { this.properties.maximization.maximizeIcon = new IconModel(); }
    this.properties.maximization.maximizeIcon.name = value;
  }
/**
 * Maximize icon size.
 * @param {?} value
 * @return {?}
 */
set maximizeIconSize(value: string) {
    if (this.properties.maximization == null) { this.properties.maximization = {}; }
    if (this.properties.maximization.maximizeIcon == null) { this.properties.maximization.maximizeIcon = new IconModel(); }
    this.properties.maximization.maximizeIcon.size = value;
  }
/**
 * Minimize icon name.
 * @param {?} value
 * @return {?}
 */
set minimizeIconName(value: string) {
    if (this.properties.maximization == null) { this.properties.maximization = {}; }
    if (this.properties.maximization.minimizeIcon == null) { this.properties.maximization.minimizeIcon = new IconModel(); }
    this.properties.maximization.minimizeIcon.name = value;
  }
/**
 * Minimize icon size.
 * @param {?} value
 * @return {?}
 */
set minimizeIconSize(value: string) {
    if (this.properties.maximization == null) { this.properties.maximization = {}; }
    if (this.properties.maximization.minimizeIcon == null) { this.properties.maximization.minimizeIcon = new IconModel(); }
    this.properties.maximization.minimizeIcon.size = value;
  }
/**
 * Wheather to show expandable section.
 * @param {?} value
 * @return {?}
 */
set showExpandable(value: boolean) {
    if (this.properties.expandable == null) { this.properties.expandable = {}; }
    this.properties.expandable.show = value;
  }
/**
 * Order of expandable section. Means flex system order property. Order numbers can be: 0, 1, 2, 3, 4, 5, 6.
 * @param {?} value
 * @return {?}
 */
set expandableOrder(value: number) {
    if (this.properties.expandable == null) { this.properties.expandable = {}; }
    this.properties.expandable.order = value;
  }
/**
 * Wheather toolbar is in expanded mode.
 * @param {?} value
 * @return {?}
 */
set expanded(value: boolean) {
    if (this.properties.expandable == null) { this.properties.expandable = {}; }
    this.properties.expandable.expanded = value;
  }
/**
 * Expandable icon name.
 * @param {?} value
 * @return {?}
 */
set expandableIconName(value: string) {
    if (this.properties.expandable == null) { this.properties.expandable = {}; }
    if (this.properties.expandable.icon == null) { this.properties.expandable.icon = new IconModel(); }
    this.properties.expandable.icon.name = value;
  }
/**
 * Expandable icon size.
 * @param {?} value
 * @return {?}
 */
set expandableIconSize(value: string) {
    if (this.properties.expandable == null) { this.properties.expandable = {}; }
    if (this.properties.expandable.icon == null) { this.properties.expandable.icon = new IconModel(); }
    this.properties.expandable.icon.size = value;
  }
/**
 * Info output event emitter
 */
onInfo: EventEmitter<any> = new EventEmitter<any>();
/**
 * Menu item output event emitter
 */
onItemClick: EventEmitter<any> = new EventEmitter<any>();
/**
 * Maximize toogle output event emitter
 */
onMaximizeToggle: EventEmitter<any> = new EventEmitter<any>();
/**
 * Expand toggle output event emitter
 */
onExpandToggle: EventEmitter<any> = new EventEmitter<any>();
/**
 * Close output event emitter
 */
onClose: EventEmitter<any> = new EventEmitter<any>();
/**
 * Help output event emitter
 */
onHelp: EventEmitter<any> = new EventEmitter<any>();
/**
 * @hidden 
 * If content linked to the toolbar is maximized.
 */
_toolbarExpanded = true;
/**
 * @hidden 
 * If toolbar is maximized and goes over all in the page.
 */
_toolbarMaximized = false;
/**
 * @hidden
 * @param {?} elementRef
 * @param {?} templateService
 * @param {?} renderer
 */
constructor(public elementRef: ElementRef, /**@hidden */ templateService: TemplateService,
public renderer: Renderer) {
    super(elementRef, templateService);
    this.properties = new ToolbarModel();
    this.styling = new ToolbarStylingModel();
    // console.log('toolbar constr', this.properties, this.properties != null);
  }
/**
 * @hidden
 * @return {?}
 */
ngOnInit() {
    // console.log('toolbar start');
    this.getMyTemplate('toolbar', ToolbarTemplates).then(() => {
      this.mergeProperties(this.activeTemplate.property, this.properties, ' **TOOLBAR** ').then(sampler => {
        // console.log('TOOLBAR', this);
      });
    });
  }
/**
 * @hidden 
 * Function to emit info event
 * @param {?} event  it is click event
 * @return {?}
 */
info(event) {
    this.onInfo.emit(event);
  }
/**
 * @hidden 
 * Function to emit help event
 * @param {?} event  it is click event
 * @return {?}
 */
help(event) {
    this.onHelp.emit(event);
  }
/**
 * @hidden 
 * Function to emit menu item action event
 * @param {?} event  it is click event
 * @return {?}
 */
itemAction(event) {
    this.onItemClick.emit(event);
  }
/**
 * Function to toggle content connected to the toolbar
 * @param {?} event  it is boolean value of toolbar expanded state
 * @return {?}
 */
expandToggle(event) {
    if (this.properties && this.properties.expandable) {
      this.properties.expandable.expanded = !this.properties.expandable.expanded;
      this.onExpandToggle.emit({ event: event, expanded: this.properties.expandable.expanded });
    }
  }
/**
 * @hidden 
 * Function to collapse/expand toolbar itself
 * @param {?} event  it is click event
 * @return {?}
 */
dblclick(event) {
    window.getSelection().removeAllRanges();
    event.preventDefault();
    event.stopPropagation();
    this.expandToggle(event);
  }
/**
 * @hidden 
 * Function to maximize/minimize component with toolbar
 * @param {?} event  it is click event
 * @return {?}
 */
maximizeToggle(event) {
    if (this.properties && this.properties.maximization) {
      this.properties.maximization.maximized = !this.properties.maximization.maximized;
      this.toolbarMaximizedClass = this.properties.maximization.maximized ? 'cf-toolbar-maximized' : 'cf-toolbar-minimized';
      this.onMaximizeToggle.emit({ event: event, maximized: this.properties.maximization.maximized });
    }
  }
/**
 * @hidden 
 * Function to emit close event
 * @param {?} event  it is click event
 * @return {?}
 */
close(event) {
    this.onClose.emit(event);
    // this.elementRef.nativeElement.remove();
    // this._toolbarClosed = true;
  }
/**
 * @hidden 
 * Function to return material name of toolbar type
 * @param {?} name   name of toolbar type: info/warning/error
 * @return {?}
 */
getToolbarType(name) {
    return name === 'error' ? 'mat-warn' : name === 'warning' ? 'mat-accent' : 'mat-primary';
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  selector: 'cf-toolbar',
  template: "<div *ngIf=\"_inited && properties?.display\" class=\"{{activeTemplate.style?.container?.class}} {{styling?.container?.class}} {{getDynamicClasses(activeTemplate.style?.container?.dynamicClass)}} {{getDynamicClasses(styling?.container?.dynamicClass)}}\" [ngClass]=\"'cf-toolbar-container'\"> <md-toolbar class=\"mat-toolbar {{getToolbarType(properties?.type)}} {{activeTemplate.style?.toolbar?.class}} {{styling?.toolbar?.class}} {{getDynamicClasses(activeTemplate.style?.toolbar?.dynamicClass)}} {{getDynamicClasses(styling?.toolbar?.dynamicClass)}}\" [ngClass]=\"'cf-toolbar'\" (dblclick)=\"dblclick($event)\"> <div *ngIf=\"properties?.info && properties?.info?.show\" class=\"{{activeTemplate.style?.toolbar?.info?.class}} {{styling?.toolbar?.info?.class}} {{getDynamicClasses(activeTemplate.style?.toolbar?.info?.dynamicClass)}} {{getDynamicClasses(styling?.toolbar?.info?.dynamicClass)}} flex-order-{{properties?.info?.order}}\" [ngClass]=\"'cf-toolbar-info'\"> <cf-icon [properties]=\"properties?.info?.icon\" [styling]=\"styling?.toolbar?.info?.icon\" (click)=\"info($event)\"></cf-icon> </div> <div *ngIf=\"properties?.content?.show\" class=\"{{activeTemplate.style?.toolbar?.content?.class}} {{styling?.toolbar?.content?.class}} {{getDynamicClasses(activeTemplate.style?.toolbar?.content?.dynamicClass)}} {{getDynamicClasses(styling?.toolbar?.content?.dynamicClass)}} flex-order-{{properties?.content?.order}}\" [ngClass]=\"'cf-toolbar-content'\"> <div class=\"{{activeTemplate.style?.toolbar?.content?.title?.class}} {{styling?.toolbar?.content?.title?.class}} {{getDynamicClasses(activeTemplate.style?.toolbar?.content?.title?.dynamicClass)}} {{getDynamicClasses(styling?.toolbar?.content?.title?.dynamicClass)}}\" [ngClass]=\"'cf-toolbar-title'\" [innerHtml]=\"properties?.content?.title\"></div> <div class=\"{{activeTemplate.style?.toolbar?.content?.template?.class}} {{styling?.toolbar?.content?.template?.class}} {{getDynamicClasses(activeTemplate.style?.toolbar?.content?.template?.dynamicClass)}} {{getDynamicClasses(styling?.toolbar?.content?.template?.dynamicClass)}}\" [ngClass]=\"'cf-toolbar-template'\"> <span [ngTemplateOutlet]=\"properties?.content?.template\"></span> <ng-content></ng-content> </div> </div> <div *ngIf=\"properties?.actions?.show\" class=\"{{activeTemplate.style?.toolbar?.actions?.class}} {{styling?.toolbar?.actions?.class}} {{getDynamicClasses(activeTemplate.style?.toolbar?.actions?.dynamicClass)}} {{getDynamicClasses(styling?.toolbar?.actions?.dynamicClass)}} flex-order-{{properties?.actions?.order}}\" [ngClass]=\"'cf-toolbar-actions'\"> <cf-menu [properties]=\"properties?.actions?.menu\" [styling]=\"styling?.toolbar?.actions?.menu\" (onItemClick)=\"itemAction($event)\"></cf-menu> </div> <div *ngIf=\"properties?.close?.show\" class=\"{{activeTemplate.style?.toolbar?.close?.class}} {{styling?.toolbar?.close?.class}} {{getDynamicClasses(activeTemplate.style?.toolbar?.close?.dynamicClass)}} {{getDynamicClasses(styling?.toolbar?.close?.dynamicClass)}} flex-order-{{properties?.close?.order}}\" [ngClass]=\"'cf-toolbar-close'\"> <cf-icon [properties]=\"properties?.close?.icon\" [styling]=\"styling?.toolbar?.close?.icon\" (click)=\"close($event)\"></cf-icon> </div> <div *ngIf=\"properties?.help?.show\" class=\"{{activeTemplate.style?.toolbar?.help?.class}} {{styling?.toolbar?.help?.class}} {{getDynamicClasses(activeTemplate.style?.toolbar?.help?.dynamicClass)}} {{getDynamicClasses(styling?.toolbar?.help?.dynamicClass)}} flex-order-{{properties?.help?.order}}\" [ngClass]=\"'cf-toolbar-help'\"> <cf-icon [properties]=\"properties?.help?.icon\" [styling]=\"styling?.toolbar?.help?.icon\" (click)=\"help($event)\"></cf-icon> </div> <div *ngIf=\"properties?.maximization?.show\" class=\"{{activeTemplate.style?.toolbar?.maximization?.class}} {{styling?.toolbar?.maximization?.class}} {{getDynamicClasses(activeTemplate.style?.toolbar?.maximization?.dynamicClass)}} {{getDynamicClasses(styling?.toolbar?.maximization?.dynamicClass)}} flex-order-{{properties?.maximization?.order}}\" [ngClass]=\"'cf-toolbar-maximization'\"> <cf-icon *ngIf=\"properties?.maximization?.maximized\" [properties]=\"properties?.maximization?.minimizeIcon\" [styling]=\"styling?.toolbar?.maximization?.minimizeIcon\" (click)=\"maximizeToggle($event)\"></cf-icon> <cf-icon *ngIf=\"!properties?.maximization?.maximized\" [properties]=\"properties?.maximization?.maximizeIcon\" [styling]=\"styling?.toolbar?.maximization?.maximizeIcon\" (click)=\"maximizeToggle($event)\"></cf-icon> </div> <div *ngIf=\"properties?.expandable?.show\" class=\"{{activeTemplate.style?.toolbar?.expandable?.class}} {{styling?.toolbar?.expandable?.class}} {{getDynamicClasses(activeTemplate.style?.toolbar?.expandable?.dynamicClass)}} {{getDynamicClasses(styling?.toolbar?.expandable?.dynamicClass)}} flex-order-{{properties?.expandable?.order}} {{properties?.expandable?.expanded ? 'cf-toolbar-expanded' : 'cf-toolbar-collapsed'}}\" [ngClass]=\"'cf-toolbar-expandable'\"> <cf-icon [properties]=\"properties?.expandable?.icon\" [styling]=\"styling?.toolbar?.expandable?.icon\" (click)=\"expandToggle($event)\"></cf-icon> </div> </md-toolbar> </div>",
  styles: ["/** *How to define a color palette in sass */ :host /deep/ .mat-toolbar { padding: 0 16px; } :host /deep/ .mat-toolbar-row { height: auto; min-height: 64px; line-height: 48px; } :host /deep/ .cf-toolbar-info, :host /deep/ .cf-toolbar-content, :host /deep/ .cf-toolbar-actions, :host /deep/ .cf-toolbar-close, :host /deep/ .cf-toolbar-help, :host /deep/ .cf-toolbar-maximization, :host /deep/ .cf-toolbar-expandable { display: flex; flex-direction: column; padding: 0px 4px; min-width: 24px; } :host /deep/ .cf-toolbar-info md-icon.mat-icon, :host /deep/ .cf-toolbar-content md-icon.mat-icon, :host /deep/ .cf-toolbar-actions md-icon.mat-icon, :host /deep/ .cf-toolbar-close md-icon.mat-icon, :host /deep/ .cf-toolbar-help md-icon.mat-icon, :host /deep/ .cf-toolbar-maximization md-icon.mat-icon, :host /deep/ .cf-toolbar-expandable md-icon.mat-icon { color: currentColor; } :host /deep/ .cf-toolbar-content { flex: 1 1 auto; width: 100%; padding: 8px 4px; overflow: hidden; } :host /deep/ .cf-toolbar-title, :host /deep/ .cf-toolbar-template { text-overflow: ellipsis; overflow: hidden; } :host /deep/ .cf-toolbar-expandable { align-items: center; justify-content: center; } :host /deep/ .cf-toolbar-expandable cf-icon { transition: all 0.3s ease; transform: rotate(-90deg); } :host /deep/ .cf-toolbar-expandable cf-icon md-icon { display: flex; align-items: center; justify-content: center; } :host /deep/ .cf-toolbar-expandable.cf-toolbar-expanded cf-icon { transform: rotate(0deg); } :host /deep/ .cf-toolbar-actions .cf-menu-container { display: flex; } :host /deep/ .flex-order-0 { order: 0; } :host /deep/ .flex-order-1 { order: 1; } :host /deep/ .flex-order-2 { order: 2; } :host /deep/ .flex-order-3 { order: 3; } :host /deep/ .flex-order-4 { order: 4; } :host /deep/ .flex-order-5 { order: 5; } :host /deep/ .flex-order-6 { order: 6; } @media screen and (-ms-high-contrast: active), screen and (-ms-high-contrast: none) { :ng-deep .mat-toolbar-row { height: 64px; } } "]
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: TemplateService, },
{type: Renderer, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'toolbarMaximizedClass': [{ type: HostBinding, args: ['class', ] },],
'properties': [{ type: Input },],
'styling': [{ type: Input },],
'type': [{ type: Input, args: ['type', ] },],
'showInfo': [{ type: Input, args: ['showInfo', ] },],
'infoOrder': [{ type: Input, args: ['infoOrder', ] },],
'infoIconName': [{ type: Input, args: ['infoIconName', ] },],
'infoIconSize': [{ type: Input, args: ['infoIconSize', ] },],
'showContent': [{ type: Input, args: ['showContent', ] },],
'title': [{ type: Input, args: ['title', ] },],
'template': [{ type: Input, args: ['template', ] },],
'contentOrder': [{ type: Input, args: ['contentOrder', ] },],
'showActions': [{ type: Input, args: ['showActions', ] },],
'actionsOrder': [{ type: Input, args: ['actionsOrder', ] },],
'menuIconName': [{ type: Input, args: ['menuIconName', ] },],
'menuIconSize': [{ type: Input, args: ['menuIconSize', ] },],
'showHelp': [{ type: Input, args: ['showHelp', ] },],
'helpOrder': [{ type: Input, args: ['helpOrder', ] },],
'helpIconName': [{ type: Input, args: ['helpIconName', ] },],
'helpIconSize': [{ type: Input, args: ['helpIconSize', ] },],
'showClose': [{ type: Input, args: ['showClose', ] },],
'closeOrder': [{ type: Input, args: ['closeOrder', ] },],
'closeIconName': [{ type: Input, args: ['closeIconName', ] },],
'closeIconSize': [{ type: Input, args: ['closeIconSize', ] },],
'showMaximization': [{ type: Input, args: ['showMaximization', ] },],
'maximizationOrder': [{ type: Input, args: ['maximizationOrder', ] },],
'maximized': [{ type: Input, args: ['maximized', ] },],
'maximizeIconName': [{ type: Input, args: ['maximizeIconName', ] },],
'maximizeIconSize': [{ type: Input, args: ['maximizeIconSize', ] },],
'minimizeIconName': [{ type: Input, args: ['minimizeIconName', ] },],
'minimizeIconSize': [{ type: Input, args: ['minimizeIconSize', ] },],
'showExpandable': [{ type: Input, args: ['showExpandable', ] },],
'expandableOrder': [{ type: Input, args: ['expandableOrder', ] },],
'expanded': [{ type: Input, args: ['expanded', ] },],
'expandableIconName': [{ type: Input, args: ['expandableIconName', ] },],
'expandableIconSize': [{ type: Input, args: ['expandableIconSize', ] },],
'onInfo': [{ type: Output },],
'onItemClick': [{ type: Output },],
'onMaximizeToggle': [{ type: Output },],
'onExpandToggle': [{ type: Output },],
'onClose': [{ type: Output },],
'onHelp': [{ type: Output },],
};
}

function CfToolbarComponent_tsickle_Closure_declarations() {
/** @type {?} */
CfToolbarComponent.decorators;
/**
 * @nocollapse
 * @type {?}
 */
CfToolbarComponent.ctorParameters;
/** @type {?} */
CfToolbarComponent.propDecorators;
/**
 * @hidden 
 * Binding class to Toolbar host element
 * @type {?}
 */
CfToolbarComponent.prototype.toolbarMaximizedClass;
/**
 * <p>Example of model:</p>
 * <code><pre>{{'{'}}
 * 	type: 'info',
 * 	info: {{'{'}}
 * 		show: true,
 * 		icon: this.myInfoIcon,
 * 		order: 0
 * 	{{'}'}},
 * 	content: {{'{'}}
 * 		title: 'This is my toolbar title',
 *  	template: this.myToolbarTemplate,
 * 		order: 1
 * 	{{'}'}},
 * 	actions: {{'{'}}
 * 		show: true,
 * 		menu: this.myToobarMenu,
 * 		order: 2
 * 	{{'}'}},
 * 	help: {{'{'}}
 * 		show: true,
 * 		icon: this.myHelpIcon,
 * 		order: 3
 * 	{{'}'}},
 * 	close: {{'{'}}
 * 		show: true,
 * 		icon: this.myCloseIcon,
 * 		order: 4
 * 	{{'}'}},
 * 	maximization: {{'{'}}
 * 		show: true,
 * 		maximized: false,
 * 		maximizeIcon: this.myMaxIcon,
 * 		minimizeIcon: this.myMinIcon,
 * 		order: 5
 * 	{{'}'}},
 * 	expandable: {{'{'}}
 * 		show: true,
 * 		expanded: true,
 * 		icon: this.myexpandableIcon
 * 		order: 6
 * 	{{'}'}}
 * {{'}'}}</pre></code>
 * <h4>type: string</h4>
 * Toolbar type. Can be: info, warning, error.
 * <h4>info: object</h4>
 * Optional info section.
 * <h4>info.show: boolean</h4>
 * To show / hide info section.
 * <h4>info.icon: IconModel</h4>
 * IconModel data for info icon.
 * <h4>info.order: number</h4>
 * Order of info section. Means flex system order property. Order numbers can be: 0, 1, 2, 3, 4, 5, 6.
 * <h4>content: object</h4>
 * Optional content section.
 * <h4>content.show: boolean</h4>
 * To show / hide toolbar content section.
 * <h4>content.title: string</h4>
 * Title for content section.
 * <h4>content.template: TemplateRef{{'<'}}any{{'>'}}</h4>
 * Toolbar content template.
 * <h4>content.order: number</h4>
 * Order of content section. Means flex system order property. Order numbers can be: 0, 1, 2, 3, 4, 5, 6.
 * <h4>actions: object</h4>
 * Optional actions section.
 * <h4>actions.show: boolean</h4>
 * To show / hide actions section.
 * <h4>actions.menu: MenuModel</h4>
 * MenuModel component for actions section.
 * <h4>actions.order: number</h4>
 * Order of actions section. Means flex system order property. Order numbers can be: 0, 1, 2, 3, 4, 5, 6.
 * <h4>close: object</h4>
 * Optional close icon section.
 * <h4>close.show: boolean</h4>
 * To show / hide close icon section.
 * <h4>close.icon: IconModel</h4>
 * IconModel data for close icon.
 * <h4>close.order: number</h4>
 * Order of close icon section. Means flex system order property. Order numbers can be: 0, 1, 2, 3, 4, 5, 6.
 * <h4>help: object</h4>
 * Optional help icon section.
 * <h4>help.show: boolean</h4>
 * To show / hide help icon section.
 * <h4>icon: IconModel</h4>
 * IconModel data for help icon.
 * <h4>help.order: number</h4>
 * Order of help icon section. Means flex system order property. Order numbers can be: 0, 1, 2, 3, 4, 5, 6.
 * <h4>maximization: object</h4>
 * Optional maximize/minimize icons section.
 * <h4>maximization.show: boolean</h4>
 * To show / hide maximize/minimize icons section.
 * <h4>maximization.maximized: boolean</h4>
 * Whether toolbar must be maximized.
 * <h4>maximization.minimizeIcon: IconModel</h4>
 * IconModel data for minimizeIcon.
 * <h4>maximization.maximizeIcon: IconModel</h4>
 * IconModel data for maximizeIcon.
 * <h4>maximization.order: number</h4>
 * Order of maximize/minimize icons section. Means flex system order property. Order numbers can be: 0, 1, 2, 3, 4, 5, 6.
 * <h4>expandable: object</h4>
 * Optional expandable icon section.
 * <h4>expandable.show: boolean</h4>
 * To show / hide expandable icon section.
 * <h4>expandable.expanded: boolean</h4>
 * To set toolbar to be expanded.
 * <h4>expandable.icon: IconModel</h4>
 * IconModel data for expandable icon.
 * <h4>expandable.order: number</h4>
 * Order of expandable icon section. Means flex system order property. Order numbers can be: 0, 1, 2, 3, 4, 5, 6.
 * @type {?}
 */
CfToolbarComponent.prototype.properties;
/**
 * Styling object
 * <code><pre>
 * toolbar?: {{'{'}}                             // Styling for md-toolbar
 * 	info?: {{'{'}}                               // Styling for info section
 * 		class?: string;                      // Classes for info section
 * 		dynamicClass?: any;                  // Dynamic classes for info section
 * 		icon?: IconStylingModel;             // IconStylingModel for info icon
 * 	{{'}'}};
 * 	content?: {{'{'}}                            // Styling for content section
 * 		class?: string;                      // Classes for content section
 * 		dynamicClass?: any;                  // Dynamic classes for content section
 * 		title?: StylingModel;                // StylingModel for title
 * 		template?: StylingModel;             // StylingModel for template
 * 	{{'}'}};
 * 	actions?: {{'{'}}                            // Styling for actions section
 * 		class?: string;                      // Classes for actions section
 * 		dynamicClass?: any;                  // Dynamic classes for actions section
 * 		menu?: MenuStylingModel;             // MenuStylingModel for menu section
 * 	{{'}'}};
 * 	maximizing?: {{'{'}}                         // Styling for maximizing section
 * 		class?: string;                      // Classes for maximizing section
 * 		dynamicClass?: any;                  // Dynamic classes for maximizing section
 * 		minimizeIcon?: IconStylingModel;     // IconStylingModel for minimize icon
 * 		maximizeIcon?: IconStylingModel;     // IconStylingModel for maximize icon
 * 	{{'}'}};
 * 	expandable?: {{'{'}}                         // Styling for expandable section
 * 		class?: string;                      // Classes for expandable section
 * 		dynamicClass?: any;                  // Dynamic classes for expandable section
 * 		icon?: IconStylingModel;             // IconStylingModel for expandable icon
 * 	{{'}'}};
 * 	help?: {{'{'}}                               // Styling for help section
 * 		class?: string;                      // Classes for help section
 * 		dynamicClass?: any;                  // Dynamic classes for help section
 * 		icon?: IconStylingModel;             // IconStylingModel for help icon
 * 	{{'}'}};
 * 	close?: {{'{'}}                              // Styling for close section
 * 		class?: string;                      // Classes for close section
 * 		dynamicClass?: any;                  // Dynamic classes for close section
 * 		icon?: IconStylingModel;             // IconStylingModel for close icon
 * 	{{'}'}};
 * {{'}'}};
 * </pre></code>
 * @type {?}
 */
CfToolbarComponent.prototype.styling;
/**
 * Info output event emitter
 * @type {?}
 */
CfToolbarComponent.prototype.onInfo;
/**
 * Menu item output event emitter
 * @type {?}
 */
CfToolbarComponent.prototype.onItemClick;
/**
 * Maximize toogle output event emitter
 * @type {?}
 */
CfToolbarComponent.prototype.onMaximizeToggle;
/**
 * Expand toggle output event emitter
 * @type {?}
 */
CfToolbarComponent.prototype.onExpandToggle;
/**
 * Close output event emitter
 * @type {?}
 */
CfToolbarComponent.prototype.onClose;
/**
 * Help output event emitter
 * @type {?}
 */
CfToolbarComponent.prototype.onHelp;
/**
 * @hidden 
 * If content linked to the toolbar is maximized.
 * @type {?}
 */
CfToolbarComponent.prototype._toolbarExpanded;
/**
 * @hidden 
 * If toolbar is maximized and goes over all in the page.
 * @type {?}
 */
CfToolbarComponent.prototype._toolbarMaximized;
/** @type {?} */
CfToolbarComponent.prototype.elementRef;
/** @type {?} */
CfToolbarComponent.prototype.renderer;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
